/**
 * NSW has an extra variable xlMaxWidth which doesn't exist on au/nz.
 * This trips up Typescript as we override styled-components DefaultTheme
 * per environment (au/nz/nsw). So cast each theme here as unknown.
 *
 * Because of this we can't get typescript intellisense in SKV.Core components
 * as SKV.Core has no concept of which environment it might be executed in.
 *
 * TODO : Look in to adding xlMaxWidth variable to au/nz so we don't have this issue
 *  */

import { DefaultTheme, ThemeProvider } from "styled-components";
import { PropsAreEqual } from ".";
import { auTheme, nswTheme, nzTheme } from "../themes";

export const withThemeProviderNz = <Props extends {}>(
  Component: {
    (props: Props): Exclude<JSX.Element, undefined>;
    displayName?: string;
  },
  propsAreEqual?: PropsAreEqual<Props> | false,

  componentName = Component.displayName ?? Component.name
): typeof WithThemeProviderNz => {
  const displayName = Component.displayName || "Component";

  const WithThemeProviderNz = (props: Props): JSX.Element => (
    <ThemeProvider theme={nzTheme as unknown as DefaultTheme}>
      <Component {...props} />
    </ThemeProvider>
  );

  WithThemeProviderNz.displayName = displayName;

  return WithThemeProviderNz;
};

export const withThemeProviderAu = <Props extends {}>(
  Component: {
    (props: Props): Exclude<JSX.Element, undefined>;
    displayName?: string;
  },
  propsAreEqual?: PropsAreEqual<Props> | false,

  componentName = Component.displayName ?? Component.name
): typeof WithThemeProviderAu => {
  const displayName = Component.displayName || "Component";

  const WithThemeProviderAu = (props: Props): JSX.Element => (
    <ThemeProvider theme={auTheme as unknown as DefaultTheme}>
      <Component {...props} />
    </ThemeProvider>
  );

  WithThemeProviderAu.displayName = displayName;

  return WithThemeProviderAu;
};

export const withThemeProviderNsw = <Props extends {}>(
  Component: {
    (props: Props): Exclude<JSX.Element, undefined>;
    displayName?: string;
  },
  propsAreEqual?: PropsAreEqual<Props> | false,

  componentName = Component.displayName ?? Component.name
): typeof WithThemeProviderNsw => {
  const displayName = Component.displayName || "Component";

  const WithThemeProviderNsw = (props: Props): JSX.Element => (
    <ThemeProvider theme={nswTheme as unknown as DefaultTheme}>
      <Component {...props} />
    </ThemeProvider>
  );

  WithThemeProviderNsw.displayName = displayName;

  return WithThemeProviderNsw;
};

/**
 * withThemeProvider will use whichever theme provider based on the TARGET_ENV variable.
 * On build, webpack's definePlugin will replace "process.env.TARGET_ENV" to it's resolved value
 * allowing us to remove whichever branch is not needed through dead code elimination via the minifier.
 * e.g TARGET_ENV = au,
 * if "au" === "au" -> withThemeProvider = return withThemeProviderAu
 * if "au" === "nz" = if false -> this if statement is removed (dead code)
 */
export const withThemeProvider = <Props extends {}>(
  Component: {
    (props: Props): Exclude<JSX.Element, undefined>;
    displayName?: string;
  },
  propsAreEqual?: PropsAreEqual<Props> | false,
  componentName = Component.displayName ?? Component.name
) => {
  return (props: Props): JSX.Element => {
    if (process.env.TARGET_ENV === "au") {
      return withThemeProviderAu(Component)(props);
    }

    if (process.env.TARGET_ENV === "nz") {
      return withThemeProviderNz(Component)(props);
    }

    if (process.env.TARGET_ENV === "nsw") {
      return withThemeProviderNsw(Component)(props);
    }

    throw Error(
      "withThemeProvider requires the TARGET_ENV variable to be set on process.env"
    );
  };
};
